import React, { useEffect, useState } from 'react';
import { CheckinOrderProps, OrderStepperProps } from '@helpers/Models';
import { CustomerOrderService } from '@services/CustomerService';
import { useNavigate } from 'react-router-dom';
import { FrontendRoutes } from '@helpers/Constants/Navigation';
import { Length } from '@helpers/Constants';

/**
 *
 * This component renders a stepper to visually indicate the progress of an order.
 * It highlights completed steps, the current step, and pending steps in a linear sequence.
 *
 * @param {OrderStepperProps} props - Component props.
 * @param {Array<{ step: number; name: string }>} props.steps - Array of step objects defining step numbers and labels.
 * @param {number} props.currentStep - The current active step in the order process.
 *
 * @returns {JSX.Element} A stepper UI indicating order progress.
 *
 * Features:
 * - Displays step circles with numbers or checkmarks for completed steps.
 * - Highlights the current step with a label.
 * - Connects steps with a progress line.
 */

export const OrderStepper: React.FC<OrderStepperProps> = ({
  steps,
  currentStep,
}) => {
  // const [customerId, setCustomerId] = useState<number>(0);
  const [speciesName, setSpeciesName] = useState<string>('');
  // const [quantity, setQuantity] = useState<number>(0);
  const [currentCustomizingId, setCurrentCustomizingId] = useState<number>(0);
  const navigate = useNavigate();
  const [checkinData, setCheckinData] = useState<CheckinOrderProps>();

  useEffect(() => {
    const customerID = CustomerOrderService.getCustomerId();
    if (customerID) {
      // Get order data
      const currentCustomizingId = localStorage.getItem('currentCustomizingId');

      // Ensure currentCustomizingId is a valid number
      const parsedCustomizingId = currentCustomizingId
        ? parseInt(currentCustomizingId, 10)
        : null;

      if (parsedCustomizingId !== null) {
        setSpeciesName(localStorage.getItem('speciesName') || '');
        setCurrentCustomizingId(parsedCustomizingId + 1);
      }
      const orderData = CustomerOrderService.getParsedOrderData();

      const customerOrder = orderData[customerID];
      // If check-in data exists, prepopulate it
      const existingCheckin =
        customerOrder?.species?.[currentCustomizingId ?? 1]?.checkIn;

      setCheckinData(existingCheckin);
    } else {
      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
  }, []);

  return (
    <>
      {/* <div className="flex w-full items-start flex-nowrap justify-start space-x-4 sm:space-x-8 md:space-x-12 lg:space-x-16"> */}
      <div className="font-medium text-md text-[#43474E] text-xl mt-3">
        {speciesName} - {currentCustomizingId}
        {checkinData && checkinData.inches && (
          <>
            {''} ({checkinData.feet} {Length.FEET} {checkinData.inches}{' '}
            {Length.INCHES})
          </>
        )}
      </div>
      <div className="p-4 w-full flex items-center justify-between">
        {steps.map((step, index) => (
          <div
            key={step.step}
            className="flex items-center flex-1 last:flex-none"
          >
            <div
              className={`flex-shrink-0 w-4 h-4 sm:w-6 sm:h-6 flex items-center justify-center rounded-full border-2 ${
                currentStep >= step.step
                  ? 'bg-primary text-white border-primary'
                  : 'bg-[#8d9094] text-white text-xs sm:text-sm md:text-md'
              }`}
            >
              {currentStep >= step.step ? (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.748 17.148L4.44 12.84C3.972 12.372 3.972 11.616 4.44 11.148C4.908 10.68 5.664 10.68 6.132 11.148L9.6 14.604L17.856 6.348C18.324 5.88 19.08 5.88 19.548 6.348C20.016 6.816 20.016 7.572 19.548 8.04L10.44 17.148C9.984 17.616 9.216 17.616 8.748 17.148Z"
                    fill="white"
                  />
                </svg>
              ) : (
                <span className="text-[10px] sm:text-xs">{step.step}</span>
              )}
            </div>
            {currentStep === step.step && (
              <span className="ml-2 mr-2 text-primary font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap">
                {step.name}
              </span>
            )}
            {index < steps.length - 1 && (
              <div className="flex-1 flex items-center">
                <div
                  className={`h-[2px] flex-1 mr-2 ml-2 ${currentStep > step.step ? 'bg-primary' : 'bg-gray-400'}`}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};
